import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Car, MapPin, Calendar, Clock, Scale, Euro, Plus, 
  ArrowRight, MoreVertical, Trash2, CheckCircle, Edit, Package, Sparkles, ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { tripsApi, matchingApi, parcelsApi } from '../../lib/api';
import { formatDate, formatPrice } from '../../lib/utils';
import { toast } from 'sonner';

const MyTripsPage = () => {
  const navigate = useNavigate();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTrip, setExpandedTrip] = useState(null);
  const [matchingParcels, setMatchingParcels] = useState({});
  const [loadingMatches, setLoadingMatches] = useState({});

  const fetchTrips = useCallback(async () => {
    try {
      const response = await tripsApi.getMy();
      setTrips(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTrips();
  }, [fetchTrips]);

  const fetchMatchingParcels = async (tripId) => {
    if (matchingParcels[tripId]) return; // Already loaded
    
    setLoadingMatches(prev => ({ ...prev, [tripId]: true }));
    try {
      const response = await matchingApi.getParcelsForTrip(tripId);
      setMatchingParcels(prev => ({ ...prev, [tripId]: response.data }));
    } catch (error) {
      toast.error('Erreur lors du chargement des colis');
    } finally {
      setLoadingMatches(prev => ({ ...prev, [tripId]: false }));
    }
  };

  const toggleTripExpand = (tripId) => {
    if (expandedTrip === tripId) {
      setExpandedTrip(null);
    } else {
      setExpandedTrip(tripId);
      fetchMatchingParcels(tripId);
    }
  };

  const handleAcceptParcel = async (parcelId) => {
    try {
      await parcelsApi.accept(parcelId);
      toast.success('Mission acceptée!');
      // Refresh matching parcels
      setMatchingParcels({});
      setExpandedTrip(null);
      navigate(`/dashboard/parcel/${parcelId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'acceptation');
    }
  };

  const handleDelete = async (tripId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir annuler ce trajet ?')) return;
    
    try {
      await tripsApi.delete(tripId);
      toast.success('Trajet annulé');
      fetchTrips();
    } catch (error) {
      toast.error('Erreur lors de l\'annulation');
    }
  };

  const handleComplete = async (tripId) => {
    try {
      await tripsApi.complete(tripId);
      toast.success('Trajet terminé');
      fetchTrips();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">Actif</span>;
      case 'completed':
        return <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">Terminé</span>;
      case 'cancelled':
        return <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">Annulé</span>;
      default:
        return null;
    }
  };

  const activeTrips = trips.filter(t => t.status === 'active');
  const pastTrips = trips.filter(t => t.status !== 'active');

  return (
    <div className="space-y-6 animate-fade-in" data-testid="my-trips-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-900 uppercase tracking-tight">
            Mes trajets
          </h1>
          <p className="text-slate-600 mt-1">Gérez vos trajets publiés</p>
        </div>
        <Link to="/dashboard/new-trip">
          <Button className="bg-orange-500 hover:bg-orange-600 text-white font-bold uppercase tracking-wide" data-testid="new-trip-btn">
            <Plus className="w-5 h-5 mr-2" />
            Nouveau trajet
          </Button>
        </Link>
      </div>

      {/* Active Trips */}
      <div>
        <h2 className="font-['Barlow_Condensed'] text-xl font-bold text-slate-900 uppercase mb-4">
          Trajets actifs ({activeTrips.length})
        </h2>
        
        {loading ? (
          <Card className="border-slate-200">
            <CardContent className="p-8 text-center">
              <div className="spinner mx-auto"></div>
              <p className="text-slate-500 mt-4">Chargement...</p>
            </CardContent>
          </Card>
        ) : activeTrips.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="p-8 text-center">
              <Car className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Aucun trajet actif</p>
              <Link to="/dashboard/new-trip">
                <Button className="mt-4 bg-orange-500 hover:bg-orange-600" data-testid="empty-new-trip-btn">
                  Publier un trajet
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {activeTrips.map((trip) => (
              <Card key={trip.id} className={`border-slate-200 hover:shadow-md transition-all ${expandedTrip === trip.id ? 'border-blue-300 shadow-lg' : ''}`} data-testid={`trip-${trip.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {/* Route */}
                      <div className="flex items-center gap-2 mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                            <MapPin className="w-4 h-4 text-green-600" />
                          </div>
                          <span className="font-semibold text-slate-900">{trip.departure_city}</span>
                        </div>
                        <ArrowRight className="w-5 h-5 text-slate-400" />
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                            <MapPin className="w-4 h-4 text-red-600" />
                          </div>
                          <span className="font-semibold text-slate-900">{trip.arrival_city}</span>
                        </div>
                        <div className="ml-4">
                          {getStatusBadge(trip.status)}
                        </div>
                      </div>

                      {/* Details */}
                      <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(trip.departure_date)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {trip.departure_time}
                        </span>
                        <span className="flex items-center gap-1">
                          <Scale className="w-4 h-4" />
                          {trip.available_weight} kg max
                        </span>
                        <span className="flex items-center gap-1 font-semibold text-green-600">
                          <Euro className="w-4 h-4" />
                          {trip.price_per_kg}€/kg
                        </span>
                      </div>

                      {trip.recurrent && (
                        <p className="text-xs text-orange-600 mt-2">
                          🔄 Trajet régulier: {trip.recurrent_days?.join(', ')}
                        </p>
                      )}

                      {trip.description && (
                        <p className="text-sm text-slate-500 mt-2 truncate max-w-lg">
                          {trip.description}
                        </p>
                      )}

                      {/* Matching Button */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleTripExpand(trip.id)}
                        className="mt-3 border-blue-200 text-blue-600 hover:bg-blue-50"
                        data-testid={`matching-btn-${trip.id}`}
                      >
                        <Sparkles className="w-4 h-4 mr-2" />
                        Voir les colis compatibles
                        {expandedTrip === trip.id ? (
                          <ChevronUp className="w-4 h-4 ml-2" />
                        ) : (
                          <ChevronDown className="w-4 h-4 ml-2" />
                        )}
                      </Button>
                    </div>

                    {/* Actions */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleComplete(trip.id)} className="text-blue-600">
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Marquer terminé
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDelete(trip.id)} className="text-red-600">
                          <Trash2 className="w-4 h-4 mr-2" />
                          Annuler
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  {/* Expanded Matching Parcels */}
                  {expandedTrip === trip.id && (
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      {loadingMatches[trip.id] ? (
                        <div className="text-center py-4">
                          <div className="spinner mx-auto"></div>
                          <p className="text-slate-500 text-sm mt-2">Recherche des colis compatibles...</p>
                        </div>
                      ) : matchingParcels[trip.id]?.length > 0 ? (
                        <div className="space-y-3">
                          <div className="flex items-center gap-2 text-sm font-medium text-blue-700">
                            <Package className="w-4 h-4" />
                            {matchingParcels[trip.id].length} colis compatible{matchingParcels[trip.id].length > 1 ? 's' : ''}
                          </div>
                          {matchingParcels[trip.id].map((parcel) => (
                            <div 
                              key={parcel.id} 
                              className="bg-blue-50 rounded-lg p-4 border border-blue-100"
                              data-testid={`match-parcel-${parcel.id}`}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                      <Sparkles className="w-3 h-3" />
                                      {parcel.match_score}% match
                                    </span>
                                    <span className="text-xs text-slate-500">par {parcel.sender_name}</span>
                                  </div>
                                  <div className="flex items-center gap-2 text-sm">
                                    <span className="font-medium">{parcel.pickup_address.city}</span>
                                    <ArrowRight className="w-4 h-4 text-slate-400" />
                                    <span className="font-medium">{parcel.delivery_address.city}</span>
                                  </div>
                                  <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                                    <span>{parcel.weight} kg</span>
                                    <span>{formatDate(parcel.pickup_date)}</span>
                                  </div>
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    {parcel.match_reasons?.slice(0, 3).map((reason, idx) => (
                                      <span key={idx} className="text-xs text-slate-600 bg-white px-2 py-0.5 rounded">
                                        {reason}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                                <div className="text-right ml-4">
                                  <p className="text-lg font-bold text-green-600">{formatPrice(parcel.proposed_price || parcel.base_price)}</p>
                                  <Button
                                    size="sm"
                                    onClick={() => handleAcceptParcel(parcel.id)}
                                    className="mt-2 bg-orange-500 hover:bg-orange-600 text-white"
                                    data-testid={`accept-match-${parcel.id}`}
                                  >
                                    Accepter
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <Package className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                          <p className="text-slate-500 text-sm">Aucun colis compatible pour le moment</p>
                          <p className="text-slate-400 text-xs mt-1">De nouveaux colis sont ajoutés régulièrement</p>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Past Trips */}
      {pastTrips.length > 0 && (
        <div>
          <h2 className="font-['Barlow_Condensed'] text-xl font-bold text-slate-900 uppercase mb-4">
            Trajets passés ({pastTrips.length})
          </h2>
          <div className="grid gap-4">
            {pastTrips.map((trip) => (
              <Card key={trip.id} className="border-slate-200 opacity-75" data-testid={`past-trip-${trip.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-medium text-slate-700">{trip.departure_city}</span>
                        <ArrowRight className="w-4 h-4 text-slate-400" />
                        <span className="font-medium text-slate-700">{trip.arrival_city}</span>
                        <div className="ml-2">
                          {getStatusBadge(trip.status)}
                        </div>
                      </div>
                      <p className="text-sm text-slate-500">
                        {formatDate(trip.departure_date)} à {trip.departure_time}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MyTripsPage;
